import { observer } from "mobx-react-lite"

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/dropdown-menu"
import { SortableTableHeader } from "@/components/sortable-table-header"
import { Compound as Table, TableBody, TableHeader } from "@/components/table"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { cn } from "@/domains/library"
import { ILock, LockStore } from "@/domains/lock"
import { LockTableRow } from "@/domains/lock-page/components/lock-table-row"
import { TableProcessor } from "@/domains/table-processor"
import { useVestedToken } from "@/hooks/use-vested-token"
import { LockStoreWithStats } from "@/pages/lock"

export const LockTable = observer(() => {
  const scaleApp = useScaleApplication()
  const vestedToken = useVestedToken()
  const sortBy = scaleApp.Pages.Lock.sortBy

  const locks = scaleApp.Lock.Repository.getAll()
  const veTotalSupply = scaleApp.Stats.veTotalSupply

  const processed = TableProcessor({
    rows: locks,
    // @todo - move this calc into the lens
    modifiers: {
      preTotals: [
        {
          name: "inject calculated stats",
          modifier: (row: LockStoreWithStats) => {
            row.influence = row.calculateInfluence(veTotalSupply)
          },
        },
      ],
    },
    sort: sortBy,
  })

  return (
    <TooltipProvider>
      <div className="relative overflow-x-auto">
        <Table.Root className="w-full min-w-[1000px]">
          <Table.Head>
            <Table.HeaderRow>
              <Table.Th className="max-w-[80px] px-0 sm:px-0" classNameInner="justify-center" isSortDisabled={true}>
                &nbsp;
              </Table.Th>
              <Table.Th isSortDisabled={true} classNameInner="justify-start">
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <div
                      className={cn(
                        "peer relative h-[22px] w-[22px] shrink-0 cursor-pointer",
                        "rounded-md",
                        "border border-p100 hover:border-p100",
                        "bg-p200 hover:bg-p100-light",
                      )}
                    >
                      <svg
                        className={cn(
                          "pointer-events-none absolute ml-[0.5px] mt-[1px] block h-5 w-5 stroke-t300 outline-none",
                        )}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="18 6 10 17 5 12"></polyline>
                      </svg>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={() => {
                        const nftIds = scaleApp.Lock.Repository.getAll().map((nft) => nft.id)
                        scaleApp.Pages.Lock.setSelectedNfts(nftIds)
                      }}
                    >
                      All
                      <span className="ml-1 text-t300">({scaleApp.Lock.Repository.countAll})</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => scaleApp.Pages.Lock.clearSelectedNfts()}>None</DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const nftIds = scaleApp.Lock.Repository.getAll()
                          .filter((nft) => !nft.isMaxLocked && !nft.hasExpired)
                          .map((nft) => nft.id)
                        scaleApp.Pages.Lock.setSelectedNfts(nftIds)
                      }}
                    >
                      Can max lock <span className="ml-1 text-t300">({scaleApp.Lock.Repository.countCanMaxLock})</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const nftIds = scaleApp.Lock.Repository.getAll()
                          .filter((nft) => nft.hasVotedThisEpoch)
                          .map((nft) => nft.id)
                        scaleApp.Pages.Lock.setSelectedNfts(nftIds)
                      }}
                    >
                      Voted <span className="ml-1 text-t300">({scaleApp.Lock.Repository.countVoted})</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const nftIds = scaleApp.Lock.Repository.getAll()
                          .filter((nft) => !nft.hasVotedThisEpoch && !nft.hasExpired)
                          .map((nft) => nft.id)
                        scaleApp.Pages.Lock.setSelectedNfts(nftIds)
                      }}
                    >
                      Not voted <span className="ml-1 text-t300">({scaleApp.Lock.Repository.countNotVoted})</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const nftIds = scaleApp.Lock.Repository.getAll()
                          .filter((nft) => nft.isScalest)
                          .map((nft) => nft.id)
                        scaleApp.Pages.Lock.setSelectedNfts(nftIds)
                      }}
                    >
                      Scalest <span className="ml-1 text-t300">({scaleApp.Lock.Repository.countScalest})</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const nftIds = scaleApp.Lock.Repository.getAll()
                          .filter((nft) => nft.isScalar)
                          .map((nft) => nft.id)
                        scaleApp.Pages.Lock.setSelectedNfts(nftIds)
                      }}
                    >
                      Scalar <span className="ml-1 text-t300">({scaleApp.Lock.Repository.countScalar})</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </Table.Th>
              <SortableTableHeader
                classNameInner="justify-start"
                sortBy={sortBy}
                sortColumn="id"
                onClick={() => {
                  scaleApp.Pages.Lock.setSortByFromUi({
                    key: "id",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }}
              >
                {vestedToken.symbol}
              </SortableTableHeader>

              <SortableTableHeader
                classNameInner="justify-end"
                sortBy={sortBy}
                sortColumn="lockValue"
                onClick={() => {
                  scaleApp.Pages.Lock.setSortByFromUi({
                    key: "lockValue",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }}
              >
                Voting Power
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="justify-end"
                sortBy={sortBy}
                sortColumn="lockAmount"
                onClick={() => {
                  scaleApp.Pages.Lock.setSortByFromUi({
                    key: "lockAmount",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }}
              >
                Balance
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="justify-center"
                sortBy={sortBy}
                sortColumn="power"
                onClick={() => {
                  scaleApp.Pages.Lock.setSortByFromUi({
                    key: "power",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }}
              >
                Power
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="justify-center"
                sortBy={sortBy}
                sortColumn="hasVotedThisEpoch"
                onClick={() => {
                  scaleApp.Pages.Lock.setSortByFromUi({
                    key: "hasVotedThisEpoch",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }}
              >
                Voted?
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="justify-end"
                sortBy={sortBy}
                sortColumn="lockEnds"
                onClick={() => {
                  scaleApp.Pages.Lock.setSortByFromUi({
                    key: "lockEnds",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }}
              >
                Expires
              </SortableTableHeader>

              <SortableTableHeader
                classNameInner="justify-end"
                sortBy={sortBy}
                sortColumn="influence"
                onClick={() => {
                  scaleApp.Pages.Lock.setSortByFromUi({
                    key: "influence",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }}
              >
                Influence
              </SortableTableHeader>

              <TableHeader isSortDisabled={true} classNameInner="justify-center">
                Level
              </TableHeader>
            </Table.HeaderRow>
          </Table.Head>
          <TableBody className="text-t100">
            {processed.rows.map((nft: ILock) => {
              return <LockTableRow key={nft.id} nft={nft} />
            })}
          </TableBody>
        </Table.Root>
      </div>
    </TooltipProvider>
  )
})

export default LockTable
